/**
 * A MultiPro's own safety rules (firmware V0.75+, set up with the Toolbox): what each does, and
 * for a rule that is playing up, turning it off for a while (the board turns it back on by
 * itself, even with no link), off until turned on, on again, or resetting it. Owners,
 * installers and FMS staff. A reason is needed to turn one off; it's shown on the farm and kept
 * in the activity log. Nothing changes on screen until the board reports the change.
 */
import { useState } from "react";
import { api, ApiError, type BoardRule, type CommandRecord, type Device } from "../api.ts";
import { FINAL } from "../control.ts";
import { ago, clock } from "../format.ts";
import { Section, useLoad } from "../ui.tsx";
import { Lifecycle, useCommandWatch, useListenOnly } from "./control.tsx";
import { durationText } from "../../../../packages/fms-protocol/src/safety-rules-v2.ts";

const message = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Something went wrong.");

/** How long to turn a rule off for. "until" = until someone turns it back on. */
const HOW_LONG: Array<[string, string]> = [
  ["30", "30 minutes"],
  ["60", "1 hour"],
  ["120", "2 hours"],
  ["240", "4 hours"],
  ["480", "8 hours"],
  ["1440", "1 day"],
  ["4320", "3 days"],
  ["10080", "1 week"],
  ["until", "Until it's turned back on"],
];

export function BoardRulesCard({ device, now, noSignal = false }: { device: Device; now: number; noSignal?: boolean }) {
  const data = useLoad(() => api.boardRules(device.devEui).catch(() => null), [device.devEui], 30_000);
  const listenOnly = useListenOnly();
  const { cmds, track } = useCommandWatch(device.devEui, data.reload);
  const [asking, setAsking] = useState<{ rule: number; action: "off" | "on" | "reset" } | null>(null);
  const [howLong, setHowLong] = useState("240");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<{ rule: number; text: string } | null>(null);
  const [refreshing, setRefreshing] = useState<CommandRecord | null>(null);

  const d = data.data;
  if (device.model !== "multipro" || !d) return null;
  if (!d.supported) {
    // Only say something on a board that has rules the app can't reach: older firmware.
    return null;
  }
  // No signal: the rules' state shown may be old, so nothing is changed from it.
  const blocked = listenOnly || !d.drives || noSignal;

  const send = async (rule: number, fn: () => Promise<{ command: CommandRecord }>) => {
    setError(null);
    try {
      track(String(rule), (await fn()).command);
      setAsking(null);
      setReason("");
    } catch (e) {
      setError({ rule, text: message(e) });
    }
  };

  return (
    <Section title="Safety rules on the board">
      <div className="muted small" style={{ marginBottom: 8 }}>
        These run on the board itself, with or without comms. If one is playing up, it can be turned off here for a while instead of a site visit: the board turns it back on by itself when the time is up, even if it can't reach the app by then.
      </div>
      {blocked && (
        <div className="banner warn small">
          {noSignal && !listenOnly && d.drives ? "Changes need signal." : "Changes are off for now: Think-Control is only listening while FMS-Control still runs this farm."}
        </div>
      )}
      {d.rules.length === 0 ? (
        <div className="card empty">
          The board hasn't reported any safety rules.{" "}
          <button
            className="btn ghost"
            disabled={blocked || (refreshing !== null && !FINAL.has(refreshing.status))}
            onClick={async () => {
              try {
                setRefreshing((await api.refreshBoardRules(device.devEui)).command);
              } catch (e) {
                setError({ rule: 0, text: message(e) });
              }
            }}
          >
            Ask the board
          </button>
        </div>
      ) : (
        <div className="card list">
          {d.rules.map((r) => (
            <RuleRow
              key={r.rule}
              r={r}
              now={now}
              canChange={d.canChange && !blocked}
              cmd={cmds[String(r.rule)]}
              asking={asking?.rule === r.rule ? asking.action : null}
              onAsk={(action) => {
                setError(null);
                setAsking({ rule: r.rule, action });
              }}
              onCancel={() => setAsking(null)}
              howLong={howLong}
              setHowLong={setHowLong}
              reason={reason}
              setReason={setReason}
              error={error?.rule === r.rule ? error.text : null}
              onConfirm={(action) =>
                send(r.rule, () =>
                  action === "off"
                    ? howLong === "until"
                      ? api.changeBoardRule(device.devEui, r.rule, "off", { reason })
                      : api.changeBoardRule(device.devEui, r.rule, "off-for", { minutes: Number(howLong), reason })
                    : api.changeBoardRule(device.devEui, r.rule, action),
                )
              }
            />
          ))}
        </div>
      )}
      {error?.rule === 0 && <div className="banner crit small">{error.text}</div>}
      {!d.canChange && d.rules.length > 0 && <div className="muted small">Only the farm's owner, an installer or FMS can turn these off or on.</div>}
    </Section>
  );
}

function RuleRow(props: {
  r: BoardRule;
  now: number;
  canChange: boolean;
  cmd: CommandRecord | undefined;
  asking: "off" | "on" | "reset" | null;
  onAsk: (a: "off" | "on" | "reset") => void;
  onCancel: () => void;
  onConfirm: (a: "off" | "on" | "reset") => void;
  howLong: string;
  setHowLong: (v: string) => void;
  reason: string;
  setReason: (v: string) => void;
  error: string | null;
}) {
  const { r, now, cmd, asking } = props;
  const busy = cmd !== undefined && !FINAL.has(cmd.status);
  const state = !r.enabled
    ? r.offUntil
      ? { tone: "warn", text: `Off for ${durationText(Math.max(1, Math.ceil((r.offUntil - now) / 60_000)))} more` }
      : { tone: "warn", text: "Off" }
    : r.latched
      ? { tone: "crit", text: "Tripped: waiting for a reset" }
      : r.active
        ? { tone: "crit", text: "Acting now" }
        : { tone: "good", text: "On" };
  return (
    <div className="ctl" style={{ flexWrap: "wrap" }}>
      <div style={{ minWidth: 0, flex: "1 1 260px" }}>
        <div className="t">
          Rule {r.rule} <span className={`pill ${state.tone}`}>{state.text}</span>
        </div>
        <div className="d">{r.description ?? "What it does isn't known yet: open the Toolbox on this board once and it's filled in."}</div>
        {!r.enabled && (
          <div className="d">
            {r.changedBy ? `Turned off by ${r.changedBy}${r.reason ? `: “${r.reason}”` : ""}${r.changedAt ? `, ${ago(r.changedAt, now)}` : ""}. ` : ""}
            {r.offUntil ? `Back on at ${clock(r.offUntil, now)}, by the board itself.` : "Stays off until someone turns it back on."}
          </div>
        )}
      </div>
      {props.canChange && !asking && (
        <div className="ctl-actions">
          {r.enabled ? (
            <button className="btn" disabled={busy} onClick={() => props.onAsk("off")}>
              Turn off…
            </button>
          ) : (
            <button className="btn primary" disabled={busy} onClick={() => props.onAsk("on")}>
              Turn back on
            </button>
          )}
          {r.latched && (
            <button className="btn" disabled={busy} onClick={() => props.onAsk("reset")}>
              Reset
            </button>
          )}
        </div>
      )}
      {asking && (
        <div className="ctl-note" style={{ flexBasis: "100%" }}>
          {asking === "off" ? (
            <>
              <div style={{ marginBottom: 6 }}>
                Turn off <b>rule {r.rule}</b>? While it's off it won't protect anything: what it would switch off keeps running.
              </div>
              <div className="frow" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <label className="small">
                  For{" "}
                  <select value={props.howLong} onChange={(e) => props.setHowLong(e.target.value)} aria-label="How long to turn it off for">
                    {HOW_LONG.map(([v, t]) => (
                      <option key={v} value={v}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>
                <input
                  style={{ flex: "1 1 220px" }}
                  value={props.reason}
                  onChange={(e) => props.setReason(e.target.value)}
                  placeholder="Why? e.g. pressure sensor faulty, tech coming Thursday"
                  aria-label="Why it's being turned off"
                  maxLength={200}
                />
              </div>
            </>
          ) : asking === "on" ? (
            <div>
              Turn <b>rule {r.rule}</b> back on? It starts protecting again straight away.
            </div>
          ) : (
            <div>
              Reset <b>rule {r.rule}</b>? It lets go of the outputs it's holding off, so they can be switched on again.
            </div>
          )}
          <div className="ctl-actions" style={{ marginTop: 8 }}>
            <button
              className={`btn ${asking === "off" ? "danger" : "primary"}`}
              disabled={asking === "off" && props.reason.trim().length < 3}
              onClick={() => props.onConfirm(asking)}
            >
              Confirm: {asking === "off" ? (props.howLong === "until" ? "turn off" : `turn off for ${HOW_LONG.find(([v]) => v === props.howLong)?.[1]}`) : asking === "on" ? "turn on" : "reset"}
            </button>
            <button className="btn" onClick={props.onCancel}>
              Cancel
            </button>
          </div>
          <div className="muted small">It only shows as changed once the board confirms it.</div>
        </div>
      )}
      {cmd && <Lifecycle c={cmd} />}
      {props.error && <div className="banner crit small" style={{ flexBasis: "100%" }}>{props.error}</div>}
    </div>
  );
}

/** For the top of the device page: a rule turned off is hard to miss. */
export function BoardRulesOffBanner({ device, now }: { device: Device; now: number }) {
  const off = (device.safetyRules ?? []).filter((r) => !r.enabled && (r.changedBy || r.offUntil !== undefined));
  if (!off.length) return null;
  return (
    <div className="banner warn">
      {off.map((r) => (
        <div key={r.rule}>
          Safety rule {r.rule} is off{r.changedBy ? `, turned off by ${r.changedBy}` : ""}
          {r.reason ? ` (“${r.reason}”)` : ""}. {r.offUntil ? `The board turns it back on at ${clock(r.offUntil, now)}.` : "It stays off until someone turns it back on."}
        </div>
      ))}
    </div>
  );
}
